import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

// Traveling risk alert banner (Risk Analysis Build 2). Drop this at the top of a
// document screen and it surfaces any OPEN risk_items that map to that document
// for the same order. The risk owner (or management) can resolve inline; once
// resolved the banner disappears from every document the risk maps to.
//
// Props: doc (required, e.g. 'FRS' | 'GPO' | 'YarnPO' | 'T&A' | 'PPC' | 'trim procurement'),
//        gpoNo and/or estimateId to scope to one order. With neither, it shows
//        all open risks for that doc type (doc-wide queue, e.g. trims/PPC).

const CAT_LABEL = {
  commercial: 'Commercial', material_fabric: 'Material — Fabric', material_trims: 'Material — Trims',
  capacity: 'Capacity / Delivery', technical: 'Technical / Quality',
};
const RATING_LABEL = { low: 'Low', med: 'Medium', high: 'High' };
const APPROVER_ROLES = ['admin', 'management'];

export default function RiskAlertBanner({ doc, gpoNo, estimateId }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);

  const load = useCallback(() => {
    if (!doc) return;
    const params = { doc };
    if (gpoNo) params.gpo_no = gpoNo;
    if (estimateId) params.estimate_id = estimateId;
    api.get('/risk/alerts', { params }).then((r) => setItems(r.data.data || [])).catch(() => setItems([]));
  }, [doc, gpoNo, estimateId]);
  useEffect(() => { load(); }, [load]);

  const resolve = async (it) => {
    const note = window.prompt(`Resolve "${CAT_LABEL[it.category] || it.category}" risk (${it.ra_number}).\nEnter a resolution note (required):`);
    if (note === null) return;            // cancelled
    if (!note.trim()) { alert('A resolution note is required.'); return; }
    try {
      await api.post(`/risk/items/${it.id}/resolve`, { resolution_note: note.trim() });
      load();
    } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };

  if (!items.length) return null;
  return (
    <div style={{ marginBottom: 16 }}>
      {items.map((it) => {
        const high = it.rating === 'high';
        const bg = high ? '#FBE9E9' : '#FFF8E8';
        const bd = high ? '#E22D2D' : '#E0A800';
        const fg = high ? '#A32D2D' : '#854F0B';
        const canResolve = user && (user.id === it.owner_user_id || APPROVER_ROLES.includes(user.role));
        return (
          <div key={it.id} style={{ background: bg, border: `1px solid ${bd}`, borderLeft: `5px solid ${bd}`, borderRadius: 8, padding: '10px 14px', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13, color: fg }}>
              <b>⚠ Open {RATING_LABEL[it.rating] || it.rating} risk — {CAT_LABEL[it.category] || it.category}</b>
              {' · '}Owner: {it.owner_name || '—'}{it.owner_role ? ` (${it.owner_role})` : ''}
              {' · '}{it.ra_number}{it.estimate_no ? ` · ${it.estimate_no}` : ''}{it.style_no ? ` · ${it.style_no}` : ''}
              {it.conditions ? <div style={{ marginTop: 3, opacity: 0.85 }}>Conditions: {it.conditions}</div> : null}
            </div>
            {canResolve
              ? <button onClick={() => resolve(it)} style={{ padding: '6px 14px', background: fg, color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>Mark Resolved</button>
              : <span style={{ fontSize: 11, color: fg, opacity: 0.8, whiteSpace: 'nowrap' }}>Owner to resolve</span>}
          </div>
        );
      })}
    </div>
  );
}
